/** Exam / interview language stored on ExamConfig and returned by interview APIs (`EN` | `FR`). */
export const EXAM_LANGUAGES = ['EN', 'FR'] as const;
export type ExamLanguage = (typeof EXAM_LANGUAGES)[number];

export const DEFAULT_EXAM_LANGUAGE: ExamLanguage = 'EN';

export function isExamLanguage(value: unknown): value is ExamLanguage {
  return typeof value === 'string' && (EXAM_LANGUAGES as readonly string[]).includes(value);
}

export interface Question {
  id: string;
  text: string;
  type: 'spoken' | 'code-read-only' | 'code-write';
  timeMinutes: number;
  codeSnippet?: string;
  codingLanguage?: string;
  suggestedType?: string;  // LLM-recommended modality from assign-skill (persisted in config_data)
}

export interface Skill {
  id: string;
  name: string;
  questions: Question[];
}

export interface Section {
  id: string;
  name: string;
  skills: Skill[];
}

// ── Psychometric types ─────────────────────────────────────────────────────────

export interface PsychometricSectionConfig {
  test_id: string;
  test_slug: string;
  test_name: string;
  enabled?: boolean;
}

export interface PsychometricQuestionOption {
  id: number;
  text: string;
  value: number;
}

export interface PsychometricQuestion {
  id: number;
  text: string;
  dimension: string;
  left_text: string;
  right_text: string;
  options: PsychometricQuestionOption[];
  answer_type: string;
}

export interface PsychometricTest {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  version: string;
  question_count: number;
  estimated_minutes: number;
  scoring_method: string;
  supported_languages: string[];
}

export interface PsychometricTestDetail extends PsychometricTest {
  questions_data: PsychometricQuestion[];
}

// ── Exam Config ────────────────────────────────────────────────────────────────

export interface ExamConfig {
  id: string;
  job_offer_id: string;
  version: number;
  language: string;
  config_data: {
    sections: Section[];
    psychometric_sections?: PsychometricSectionConfig[];
  };
  /** How many days interviews for this job's AI test stay valid. Defaults to 7. */
  interview_expiry_days: number;
  created_at: string;
  updated_at: string;
}

export interface ExamConfigUpdate {
  language: string;
  config_data: {
    sections: Section[];
  };
  /** How many days interviews stay valid. Omit to leave unchanged. */
  interview_expiry_days?: number;
}

// Flat form shape for React Hook Form (avoids nested object issues with useFieldArray)
export interface ExamConfigFormValues {
  language: string;
  sections: Section[];
  /** How many days interviews for this job's AI test stay valid. */
  interviewExpiryDays: number;
}
